import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { makeRequest } from "../../utils/request";

export type UsageData = {
  totalDurationMs: number;
  totalDurationMinutes: number;
  totalSessions: number;
  agentBreakdown: {
    agentId: string;
    agentName: string;
    durationMs: number;
    durationMinutes: number;
    sessionCount: number;
  }[];
};

export type UsagePeriodData = {
  id?: string; // For Redux slice compatibility
  startDate: string;
  endDate: string;
  organizationId: string;
  usage: UsageData;
};

export type UsageReportParams = {
  startDate: string;
  endDate: string;
  organizationId?: string;
};

export type MonthlyUsageParams = {
  year: string;
  month: string;
  organizationId?: string;
};

export type UsageTagBreakdownItem = {
  agentId: string;
  agentName: string;
  tags: Record<string, string>;
  durationMs: number;
  durationMinutes: number;
  sessionCount: number;
};

export type UsageTagData = {
  totalDurationMs: number;
  totalDurationMinutes: number;
  totalSessions: number;
  breakdown: UsageTagBreakdownItem[];
};

export type UsageTagReportParams = {
  startDate: string;
  endDate: string;
  organizationId?: string;
  tags?: string;
};

export type MonthlyUsageTagParams = {
  year: string;
  month: string;
  organizationId?: string;
  tags?: string;
};

export type UsageState = {
  currentReport: UsagePeriodData | null;
  isLoading: boolean;
  error: string | null;
  tagReport: UsageTagData | null;
  isTagLoading: boolean;
  tagError: string | null;
};

// Async thunks
export const getUsageReport = createAsyncThunk("usage/getUsageReport", async (params: UsageReportParams) => {
  const queryString = Object.entries(params)
    .filter(([_, value]) => value !== undefined)
    .map(([key, value]) => `${key}=${encodeURIComponent(value!)}`)
    .join("&");

  const { data } = await makeRequest<{ data: UsagePeriodData }>(`/v1/usage/report?${queryString}`);
  return data;
});

export const getMonthlyUsage = createAsyncThunk("usage/getMonthlyUsage", async (params: MonthlyUsageParams) => {
  const queryString = Object.entries(params)
    .filter(([_, value]) => value !== undefined)
    .map(([key, value]) => `${key}=${encodeURIComponent(value!)}`)
    .join("&");

  const { data } = await makeRequest<{ data: UsagePeriodData }>(`/v1/usage/monthly?${queryString}`);
  return data;
});

export const getUsageReportByTags = createAsyncThunk("usage/getUsageReportByTags", async (params: UsageTagReportParams) => {
  const queryString = Object.entries(params)
    .filter(([_, value]) => value !== undefined)
    .map(([key, value]) => `${key}=${encodeURIComponent(value!)}`)
    .join("&");

  const { data } = await makeRequest<{ data: UsageTagData }>(`/v1/usage/report/by-tags?${queryString}`);
  return data;
});

export const getMonthlyUsageByTags = createAsyncThunk("usage/getMonthlyUsageByTags", async (params: MonthlyUsageTagParams) => {
  const queryString = Object.entries(params)
    .filter(([_, value]) => value !== undefined)
    .map(([key, value]) => `${key}=${encodeURIComponent(value!)}`)
    .join("&");

  const { data } = await makeRequest<{ data: UsageTagData }>(`/v1/usage/monthly/by-tags?${queryString}`);
  return data;
});

const initialState: UsageState = {
  currentReport: null,
  isLoading: false,
  error: null,
  tagReport: null,
  isTagLoading: false,
  tagError: null,
};

const usageSlice = createSlice({
  name: "usage",
  initialState,
  reducers: {
    clearUsageData: (state) => {
      state.currentReport = null;
      state.error = null;
    },
    clearTagData: (state) => {
      state.tagReport = null;
      state.tagError = null;
    },
  },
  extraReducers: (builder) => {
    // getUsageReport cases
    builder.addCase(getUsageReport.pending, (state) => {
      state.isLoading = true;
      state.error = null;
    });
    builder.addCase(getUsageReport.fulfilled, (state, action) => {
      state.isLoading = false;
      state.currentReport = action.payload;
      state.error = null;
    });
    builder.addCase(getUsageReport.rejected, (state, action) => {
      state.isLoading = false;
      state.error = action.error.message || "Failed to fetch usage report";
    });

    // getMonthlyUsage cases
    builder.addCase(getMonthlyUsage.pending, (state) => {
      state.isLoading = true;
      state.error = null;
    });
    builder.addCase(getMonthlyUsage.fulfilled, (state, action) => {
      state.isLoading = false;
      state.currentReport = action.payload;
      state.error = null;
    });
    builder.addCase(getMonthlyUsage.rejected, (state, action) => {
      state.isLoading = false;
      state.error = action.error.message || "Failed to fetch monthly usage";
    });

    // getUsageReportByTags cases
    builder.addCase(getUsageReportByTags.pending, (state) => {
      state.isTagLoading = true;
      state.tagError = null;
    });
    builder.addCase(getUsageReportByTags.fulfilled, (state, action) => {
      state.isTagLoading = false;
      state.tagReport = action.payload;
      state.tagError = null;
    });
    builder.addCase(getUsageReportByTags.rejected, (state, action) => {
      state.isTagLoading = false;
      state.tagError = action.error.message || "Failed to fetch tag usage report";
    });

    // getMonthlyUsageByTags cases
    builder.addCase(getMonthlyUsageByTags.pending, (state) => {
      state.isTagLoading = true;
      state.tagError = null;
    });
    builder.addCase(getMonthlyUsageByTags.fulfilled, (state, action) => {
      state.isTagLoading = false;
      state.tagReport = action.payload;
      state.tagError = null;
    });
    builder.addCase(getMonthlyUsageByTags.rejected, (state, action) => {
      state.isTagLoading = false;
      state.tagError = action.error.message || "Failed to fetch monthly tag usage";
    });
  },
});

export const { clearUsageData, clearTagData } = usageSlice.actions;
export const usageReducer = usageSlice.reducer;
